import * as Sentry from '@sentry/nextjs';
import { useIsMutating, useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { addToCart, getCart, removeFromCart } from '@apiCalls';
import { useAppContext } from '@contexts';
import { currencyLookup, formatValue } from '@utils';
import { handleRenderError } from '@utils/errorHandler';
import tracker from '@utils/tracking';

interface ITicketSelectorPanelProperties {
  availableTickets: ITicket[] | ITicketSummary[] | null;
}

const DefaultMaxTicketCount = 5;

const purchaseableTickets = (ticket: ITicket | ITicketSummary) => {
  const { MaxPerTransaction, Remaining } = ticket;
  return Math.min(Remaining, MaxPerTransaction ?? DefaultMaxTicketCount);
};

export const TicketSelectorPanel = ({
  availableTickets,
}: ITicketSelectorPanelProperties) => {
  try {
    return (
      <div>
        {availableTickets ? (
          availableTickets.map(ticket => (
            <TicketBlock
              key={ticket.ID}
              ticket={ticket}
              minQuantity={ticket.MinPerTransaction}
              maxQuantity={purchaseableTickets(ticket)}
              formatVal={formatValue}
            />
          ))
        ) : (
          <>No Tickets Available</>
        )}
      </div>
    );
  } catch (error) {
    return handleRenderError(error);
  }
};

const TicketBlock = ({
  formatVal,
  ticket,
  minQuantity,
  maxQuantity,
}: {
  ticket: ITicket | ITicketSummary;
  minQuantity?: number;
  maxQuantity: number;
  formatVal: (value: number, currency?: string) => string;
}) => {
  const router = useRouter();
  const {
    cartId,
    cartItemIds,
    event,
    ticketDetails: { tickets },
    setCartItemIds,
    resetPromoCode,
    setTicketDetails,
    timeslot,
  } = useAppContext();
  const ticketsSelected = tickets?.filter(
    selectedTicket => selectedTicket.ID === ticket.ID
  );
  const selectedQuantity = ticketsSelected?.length ?? 0;
  const [error, setError] = useState<string>();
  const isMutating = useIsMutating();
  const { refetch: fetchCartData } = useQuery({
    queryKey: ['cart', cartId],
    queryFn: () => getCart(cartId ?? ''),
    enabled: !!cartId,
  });

  const addToCartMutation = useMutation({
    mutationFn: () => {
      if (!cartId) {
        throw new Error("Can't add to cart without cartId");
      }
      const payload: IUpdateCartRequest = {
        Type: 'ticket',
        ItemId: ticket.ID,
      };
      if (ticket.HasTimeslot && timeslot) {
        payload.Timeslot = {
          LocalStartTime: timeslot.LocalStartTime,
          LocalEndTime: timeslot.LocalEndTime,
        };
      }
      return addToCart(cartId, payload);
    },
    onError: error => {
      let errorMessage: string;
      switch (error.message) {
        case 'TimeSlotNotAvailable': {
          errorMessage =
            'The time slot is no longer available. Please select another.';
          ticket.Available = false;
          break;
        }
        case 'TimeslotTicket': {
          errorMessage =
            'The time slot was not assigned to the ticket. Please try again later.';
          break;
        }
        case 'NotFound': {
          errorMessage =
            'Cart was not found. Please start the booking again. You will be redirected to the first step';
          setTimeout(() => {
            router.push(`/event/${event.ID}`);
          }, 5000);
          break;
        }
        case 'ParseError': {
          errorMessage =
            'The server failed to parse the data. Please try again later or choose another ticket.';
          break;
        }
        default: {
          errorMessage = 'Failed to add item to cart. Please try again later.';
        }
      }
      Sentry.captureException(new Error(errorMessage), {
        tags: {
          cartId,
          eventId: event.ID,
          ticketId: ticket.ID,
        },
      });
      setError(errorMessage);
    },
    onSuccess: response => {
      setError(undefined);
      resetPromoCode();
      if (!response) {
        Sentry.captureException(
          new Error('Add to cart API response was empty'),
          {
            tags: {
              cartId,
              eventId: event.ID,
            },
          }
        );
        return;
      }
      setCartItemIds(previous => [...previous, response.Item.Key]);
    },
  });
  const removeFromCartMutation = useMutation({
    mutationFn: async (cartItemId: string) => {
      if (!cartId) {
        throw new Error("Can't remove from cart without cartId");
      }
      return removeFromCart(cartId, cartItemId);
    },
    onError: (error, cartItemId) => {
      let errorMessage: string;
      switch (error.name) {
        case 'NotFound': {
          if (error.message === 'The item was not found') {
            // already deleted, nothing to do
            return;
          }
          errorMessage =
            'Cart was not found. Please start the booking again. You will be redirected to the first step';
          setTimeout(() => {
            router.push(`/event/${event.ID}`);
          }, 5000);
          break;
        }
        default: {
          errorMessage =
            'Failed to remove item from the cart. Please try again later.';
        }
      }
      Sentry.captureException(new Error(errorMessage), {
        tags: {
          cartId,
          cartItemId,
          eventId: event.ID,
          ticketId: ticket.ID,
        },
      });
      setError(errorMessage);
    },
    onSuccess: (response, cartItemId) => {
      setError(undefined);
      if (!response) {
        Sentry.captureException(
          new Error('Remove from cart API response was empty'),
          {
            tags: {
              cartId,
              cartItemId,
              eventId: event.ID,
            },
          }
        );
        return;
      }
      setCartItemIds(previous => {
        previous.splice(previous.indexOf(cartItemId), 1);
        return previous;
      });
    },
  });

  const handleIncrementQuantity = async () => {
    if (isMutating) {
      return;
    }
    if (selectedQuantity >= maxQuantity) {
      setError('You cannot add more of this ticket to the cart.');
      return;
    }
    let addToCartResponses: Array<ICartAddItemResponse | undefined> = [];
    if (minQuantity && selectedQuantity < minQuantity) {
      // add minQuantity tickets for ticket type
      setTicketDetails(previous => ({
        ...previous,
        tickets: [
          ...(previous.tickets ?? []),
          ...Array.from({ length: minQuantity }, () => ticket),
        ],
      }));
      const addMultiplePromises = [];
      for (let q = 0; q < minQuantity; q++) {
        addMultiplePromises.push(addToCartMutation.mutateAsync());
      }
      addToCartResponses = await Promise.all(addMultiplePromises);
    } else {
      // add single ticket for ticket type
      setTicketDetails(previous => ({
        ...previous,
        tickets: [...(previous.tickets || []), ticket],
      }));
      addToCartResponses[0] = await addToCartMutation.mutateAsync();
    }
    await fetchCartData();
    tracker.trackAddToCart({
      addToCartResponses,
      event,
      currency: ticket.Currency,
    });
  };

  const handleDecrementQuantity = async () => {
    if (isMutating) {
      return;
    }
    if (selectedQuantity === 0) {
      return;
    }
    const index = tickets?.findIndex(
      selectedTicket => selectedTicket.ID === ticket.ID
    );
    if (index === undefined) {
      return;
    }
    if (!minQuantity || selectedQuantity > minQuantity) {
      // remove single ticket for ticket type at index
      setTicketDetails(previous => ({
        ...previous,
        tickets: (previous.tickets || []).filter(
          (_, index_) => index_ !== index
        ),
      }));
    } else if (selectedQuantity === minQuantity) {
      // remove all tickets for ticket type
      setTicketDetails(previous => ({
        ...previous,
        tickets: (previous.tickets || []).filter(
          selectedTicket => selectedTicket.ID !== ticket.ID
        ),
      }));
    }
    await removeFromCartMutation.mutateAsync(cartItemIds[index]); // FIXME: cart ticket ID
  };

  return (
    <>
      <div
        className={`checkoutTicketType ${
          ticket.Remaining <= 0 || !ticket.Available ? ' isUnavailable' : ''
        }${ticket.DiscountedAmount > 0 ? ' discounted' : ''}`}
        role='button'
      >
        <div className='ticketInformation'>
          <div className='ticketDescription'>
            <div className='ticketName'>
              <h2>{ticket.Name}</h2>
            </div>
            <div className='ticketPrice'>
              <h3 className='ticketPrice'>
                {ticket.BasePrice > 0 ? (
                  <>
                    <span className='ticketCurrency'>
                      {currencyLookup(ticket.Currency)}
                    </span>
                    <span className='ticketBasePrice'>
                      {formatVal(ticket.BasePrice)}
                    </span>
                    {ticket.DiscountedAmount > 0 && (
                      <>
                        <span className='ticketCurrency discounted'>
                          {currencyLookup(ticket.Currency)}
                        </span>
                        <span className='ticketDiscountedPrice discounted'>
                          {formatVal(ticket.DiscountedAmount)}
                        </span>
                      </>
                    )}
                    <span className='ticketFees minor'>
                      +{' '}
                      {formatVal(ticket.Fees, currencyLookup(ticket.Currency))}{' '}
                      booking fee
                    </span>
                  </>
                ) : (
                  <span className='ticketBasePrice'>Free</span>
                )}
              </h3>
            </div>
          </div>
          {ticket.Available || selectedQuantity > 0 ? (
            ticketsSelected?.length === 0 ? (
              <div className='ticketQuantitySelector'>
                <div
                  className={`ticketQuantityButton ${
                    maxQuantity === 0 && 'disabled'
                  }`}
                  role='button'
                  onClick={handleIncrementQuantity}
                >
                  <svg
                    id='iconPlus'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 100 100'
                  >
                    <path d='M75.02807,45c-6.68304,0-13.36374,.25017-20.02806,.75h-.75001v-.75h0c.49982-6.66433,.75-13.34503,.75-20.02807V4.99999C55,2.23857,52.76143,0,50,0h0C47.23857,0,45,2.23857,45,4.99999V24.97193c0,6.68304,.25017,13.36374,.75,20.02806h0v.75001h-.75001c-6.66432-.49983-13.34502-.75-20.02806-.75H5c-2.76142,0-5,2.23857-5,5H0c0,2.76143,2.23857,5,5,5H24.97193c6.68304,0,13.36374-.25017,20.02806-.75h.75001v.75h0c-.49982,6.66433-.75,13.34503-.75,20.02807v19.97194c0,2.76142,2.23857,5,5,5h0c2.76142,0,5-2.23857,5-5v-19.97194c0-6.68304-.25017-13.36374-.75-20.02806h0v-.75001h.75001c6.66432,.49983,13.34502,.75,20.02806,.75h19.97194c2.76142,0,5-2.23857,5-5h0c0-2.76143-2.23857-5-5-5h-19.97194Z' />
                  </svg>
                </div>
              </div>
            ) : (
              <div className='ticketQuantitySelector'>
                <div className='ticketQuantityButtonsContainer'>
                  <div
                    className={`ticketQuantityButton ${
                      isMutating ? 'disabled' : ''
                    }`}
                    role='button'
                    onClick={handleDecrementQuantity}
                  >
                    <svg
                      id='iconMinus'
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 100 100'
                    >
                      <rect y='45' width='100' height='10' rx='5' ry='5' />
                    </svg>
                  </div>
                  <h2 className='ticketQuantity'>{selectedQuantity}</h2>
                  <div
                    className={`ticketQuantityButton ${
                      selectedQuantity >= maxQuantity || isMutating
                        ? 'disabled'
                        : ''
                    }`}
                    role='button'
                    onClick={handleIncrementQuantity}
                  >
                    <svg
                      id='iconPlus'
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 100 100'
                    >
                      <path d='M75.02807,45c-6.68304,0-13.36374,.25017-20.02806,.75h-.75001v-.75h0c.49982-6.66433,.75-13.34503,.75-20.02807V4.99999C55,2.23857,52.76143,0,50,0h0C47.23857,0,45,2.23857,45,4.99999V24.97193c0,6.68304,.25017,13.36374,.75,20.02806h0v.75001h-.75001c-6.66432-.49983-13.34502-.75-20.02806-.75H5c-2.76142,0-5,2.23857-5,5H0c0,2.76143,2.23857,5,5,5H24.97193c6.68304,0,13.36374-.25017,20.02806-.75h.75001v.75h0c-.49982,6.66433-.75,13.34503-.75,20.02807v19.97194c0,2.76142,2.23857,5,5,5h0c2.76142,0,5-2.23857,5-5v-19.97194c0-6.68304-.25017-13.36374-.75-20.02806h0v-.75001h.75001c6.66432,.49983,13.34502,.75,20.02806,.75h19.97194c2.76142,0,5-2.23857,5-5h0c0-2.76143-2.23857-5-5-5h-19.97194Z' />
                    </svg>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className='ticketIcon'>
              <h3 className='isUnavailableMessage'>
                {ticket.Status === 'sold out' ? 'Sold Out' : 'Expired'}
              </h3>
            </div>
          )}
        </div>
        <div className='ticketNote'>
          <p>{ticket.Note}</p>
        </div>
      </div>
      {error && (
        <p className='appCheckoutStepDescription' style={{ padding: '3px' }}>
          {error}
        </p>
      )}
    </>
  );
};
